import { FC, useEffect, useState } from 'react';
import getMyUpcomingBookings from '@tsClient/my/getMyUpcomingBookings';
import PlusCircle from '@components/Icons/PlusCircle';
import CaretCircleDown from '@components/Icons/CaretCircleDown';
import Modal from '@components/Modal';
import CustomerSchedulingCalendar from '@components/CustomerSchedulingCalendar';
import useNoodleApi from '@hooks/useNoodleApi';
import { getProductById } from '@tsClient';
import ProductIcon from '@components/ProductIcon';
import s from './UnscheduledBookings.module.scss';

type Props = {
  bookings: Awaited<ReturnType<typeof getMyUpcomingBookings>>;
  onSchedule: () => Promise<void>;
};

const UnscheduledBookings: FC<Props> = ({ bookings, onSchedule }) => {
  const title = bookings[0].price?.priceTitle || 'Booking';
  const { productId } = bookings[0];

  const {
    data: productData,
    fetchingState: { isFetching: isFetchingProduct },
    getData: getProduct,
  } = useNoodleApi(getProductById);

  useEffect(() => {
    getProduct({ id: productId });
  }, [productId, getProduct]);

  const [selectedBooking, setSelectedBooking] = useState<Props['bookings'][number] | null>(null);

  const handleBookingClick = (booking: Props['bookings'][number]): void => {
    setSelectedBooking(booking);
  };

  if ((isFetchingProduct && !productData) || bookings.length === 0) {
    return null;
  }

  return (
    <>
      <details className={s.wrapper} open>
        <summary>
          <div>
            {bookings[0].product?.title && (
              <small className={s.product}>
                <ProductIcon noodleProductType={productData?.product.productTypes[0]?.noodleProductType} size={14} />
                {bookings[0].product?.title}
              </small>
            )}
            <strong>Schedule {title}</strong>
            <small>{bookings.length} available</small>
          </div>
          <CaretCircleDown size={16} weight="fill" />
        </summary>
        <ul>
          {bookings.map(b => (
            <li key={b.id}>
              <button onClick={() => handleBookingClick(b)}>
                <div />
                <PlusCircle weight="fill" size={32} />
                Schedule
              </button>
            </li>
          ))}
        </ul>
      </details>
      {selectedBooking && productData?.product && (
        <Modal title="Schedule Booking" onClose={() => setSelectedBooking(null)} >
          <div className={s.modal}>
            <CustomerSchedulingCalendar
              availabilityTimezone={productData?.product?.creator?.timezone ?? null}
              priceId={selectedBooking.priceId}
              product={productData?.product}
              booking={selectedBooking}
              onFinish={onSchedule}
            />
          </div>
        </Modal>
      )}
    </>
  );
};

export default UnscheduledBookings;
